import { useEffect, useRef } from 'react';

/**
 * Hook pour gérer l'accessibilité
 */
export const useAccessibility = () => {
  const focusRef = useRef<HTMLElement>(null);

  /**
   * Gère la navigation au clavier
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    // Échap pour fermer les modales
    if (event.key === 'Escape') {
      const modals = document.querySelectorAll('[role="dialog"]');
      const lastModal = modals[modals.length - 1] as HTMLElement;
      if (lastModal) {
        const closeButton = lastModal.querySelector(
          '[data-close-modal]'
        ) as HTMLElement;
        if (closeButton) {
          closeButton.focus();
          closeButton.click();
        }
      }
    }

    // Tab pour la navigation
    if (event.key === 'Tab') {
      const focusableElements = document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  };

  /**
   * Annonce un message aux lecteurs d'écran
   */
  const announceToScreenReader = (
    message: string,
    priority: 'polite' | 'assertive' = 'polite'
  ) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Supprimer après l'annonce
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  /**
   * Gère le focus pour les modales
   */
  const trapFocus = (element: HTMLElement) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      element.removeEventListener('keydown', handleTabKey);
    };
  };

  /**
   * Gère les raccourcis clavier
   */
  const setupKeyboardShortcuts = () => {
    const shortcuts = {
      'Ctrl+/': () => {
        announceToScreenReader(
          'Raccourcis clavier disponibles: Ctrl+1 pour Points clés, Ctrl+2 pour Trophées, Échap pour fermer'
        );
      },
      'Ctrl+1': () => {
        const textTab = document.querySelector(
          '[data-tab="text"]'
        ) as HTMLElement;
        textTab?.click();
        announceToScreenReader('Onglet Points clés sélectionné');
      },
      'Ctrl+2': () => {
        const trophiesTab = document.querySelector(
          '[data-tab="trophies"]'
        ) as HTMLElement;
        trophiesTab?.click();
        announceToScreenReader('Onglet Trophées sélectionné');
      },
    };

    const handleShortcut = (event: KeyboardEvent) => {
      const key = `${event.ctrlKey ? 'Ctrl+' : ''}${event.key}`;
      const action = shortcuts[key as keyof typeof shortcuts];
      if (action) {
        event.preventDefault();
        action();
      }
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    const cleanupShortcuts = setupKeyboardShortcuts();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      cleanupShortcuts();
    };
  }, []);

  return {
    announceToScreenReader,
    trapFocus,
    focusRef,
  };
};

/**
 * Hook pour gérer les états de chargement accessibles
 */
export const useAccessibleLoading = (
  isLoading: boolean,
  loadingMessage: string = 'Chargement en cours'
) => {
  const { announceToScreenReader } = useAccessibility();

  useEffect(() => {
    if (isLoading) {
      announceToScreenReader(loadingMessage, 'assertive');
    }
  }, [isLoading, loadingMessage, announceToScreenReader]);

  return {
    loadingProps: {
      'aria-live': 'assertive',
      'aria-busy': isLoading,
      'aria-label': isLoading ? loadingMessage : undefined,
    },
  };
};

/**
 * Hook pour gérer les erreurs accessibles
 */
export const useAccessibleErrors = () => {
  const { announceToScreenReader } = useAccessibility();

  const announceError = (error: string) => {
    announceToScreenReader(`Erreur: ${error}`, 'assertive');
  };

  const announceSuccess = (message: string) => {
    announceToScreenReader(`Succès: ${message}`, 'polite');
  };

  return {
    announceError,
    announceSuccess,
  };
};
